import classnames from 'classnames'
import { css } from 'emotion'
import * as React from 'react'
import { connect } from 'react-redux'
import MaskedTextInput, { MaskedInputProps } from 'react-text-mask'

import { trackGoogleAnalyticsEvent } from 'embed/actions'
import { useTheme } from 'embed/hooks'
import { validationDataSelector } from 'embed/selectors'
import { FieldOptions, Mask, MaskFunction } from 'embed/constants'
import { ValidationData } from 'embed/services/Validation'

import { GoogleAnalyticsActions } from '@evenfinancial/even-ts-static'
export interface Props {
  autocomplete?: string
  containerClass?: string
  dispatchTrackGoogleAnalyticsValidationError: (fieldName: string) => void
  fieldName?: string
  guide?: boolean
  inputmode?: FieldOptions['inputMode']
  label?: string
  keepCharPositions?: boolean
  mask?: Mask | MaskFunction
  onChange?: (fieldName: string, value: string, isValid: boolean) => void
  pipe?: MaskedInputProps['pipe']
  placeholder?: string
  placeholderChar?: string
  required?: boolean
  showMask?: boolean
  showValidationAsYouType?: boolean
  styles?: FormStyles
  type?: string
  validate?: (value: unknown, data: ValidationData) => boolean
  validationData: ValidationData
  getValidationMessage?: (value: string, data: ValidationData) => string | undefined
  value?: string
}

export interface FormStyles {
  field?: string
  fieldError?: string
  fieldErrorMessage?: string
  fieldFocus?: string
  fieldInput?: string
  fieldLabel?: string
}

const FormText: React.FC<Props> = ({
  autocomplete,
  containerClass,
  dispatchTrackGoogleAnalyticsValidationError,
  fieldName,
  getValidationMessage,
  guide,
  inputmode,
  keepCharPositions,
  label,
  mask,
  onChange,
  pipe,
  placeholder,
  placeholderChar,
  required,
  showMask,
  showValidationAsYouType,
  styles,
  type,
  validate,
  validationData,
  ...props
}) => {
  const [isFocused, setIsFocused] = React.useState(false)
  const [isTouched, setIsTouched] = React.useState(false)
  const [isValid, setIsValid] = React.useState(true)
  const [value, setValue] = React.useState(props.value || '')

  React.useEffect(() => {
    if (props.value !== undefined && props.value !== value) {
      setIsValid(validate?.(props.value, validationData) || (!required && props.value === ''))
      setValue(props.value)
    }
  }, [props.value])

  const formatWrapperClass = React.useCallback(
    () => [styles?.field, isFocused ? styles?.fieldFocus : '', isValid ? '' : styles?.fieldError].join(' '),
    [styles?.field, styles?.fieldFocus, styles?.fieldError, isFocused, isValid],
  )

  const handleChange = React.useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      const newVal = event ? event.currentTarget.value : ''
      const newIsValid = validate?.(newVal, validationData) || newVal === ''
      setIsValid(newIsValid)
      setValue(newVal)
      if (fieldName !== undefined) {
        onChange?.(fieldName, newVal, newIsValid)
      }
    },
    [validate, validationData, fieldName, onChange],
  )
  // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
  const handleBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      const newValue = props.value || value
      const newIsValid = isValid ? !required || newValue !== '' : false

      if (!newIsValid && fieldName !== undefined) {
        dispatchTrackGoogleAnalyticsValidationError(fieldName)
      }

      setIsFocused(false)
      setIsTouched(true)
      setIsValid(newIsValid)
    },
    [isValid, required, props.value, value, fieldName, dispatchTrackGoogleAnalyticsValidationError],
  )
  // @TODO: update params to satisfy linter requirement or comment to why this needs to stay as is
  const handleFocus = React.useCallback((event: React.FocusEvent<HTMLInputElement>) => setIsFocused(true), [])

  const validationMessage =
    value !== undefined && getValidationMessage ? getValidationMessage(value, validationData) : undefined

  const { borderRadiusSmall, labelStyles, fontSizeBase } = useTheme()
  return (
    <div className={containerClass}>
      <div className={styles?.fieldLabel} style={labelStyles}>
        {label}
      </div>
      <div className={formatWrapperClass()}>
        {mask ? (
          <MaskedTextInput
            inputMode={inputmode}
            type={type}
            mask={mask}
            name={fieldName}
            className={classnames(
              styles?.fieldInput,
              css`
                border-radius: ${borderRadiusSmall};
                font-size: ${fontSizeBase};
              `,
            )}
            placeholder={placeholder}
            value={props.value || value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            guide={guide}
            keepCharPositions={keepCharPositions}
            pipe={pipe}
            placeholderChar={placeholderChar}
            showMask={showMask}
            autoComplete={autocomplete}
          />
        ) : (
          <input
            type={type}
            name={fieldName}
            className={classnames(
              styles?.fieldInput,
              css`
                border-radius: ${borderRadiusSmall};
                font-size: ${fontSizeBase};
              `,
            )}
            placeholder={placeholder}
            value={props.value || value}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            autoComplete={autocomplete}
          />
        )}
        {validationMessage !== undefined && (isTouched || showValidationAsYouType) && (
          <div className={styles?.fieldErrorMessage}>{validationMessage}</div>
        )}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  validationData: validationDataSelector(state),
})

const mapDispatchToProps = (dispatch) => ({
  dispatchTrackGoogleAnalyticsValidationError: (fieldName) =>
    dispatch(trackGoogleAnalyticsEvent(GoogleAnalyticsActions.ValidationError, undefined, undefined, fieldName)),
})

export default connect(mapStateToProps, mapDispatchToProps)(FormText)
